import { TUploadFile } from '@/components/common/attach-file';
import { ErrorType, FormError, InputRefs } from '@/constant';
import { ApiResponseError } from '@/services';
import {
	dayjs,
	formatDate,
	showAlert,
	SUPPORT_FORMAT_DATE_LIST,
	toCamelCase,
} from '@/utils';
import {
	ChangeEventHandler,
	FocusEventHandler,
	KeyboardEventHandler,
} from 'react';
import { Value as ReactQuillValue } from 'react-quill/lib';

import { DatePickerProps, RadioChangeEvent, TimePickerProps } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';

import { Dayjs } from 'dayjs';
import { FormikErrors, FormikProps, getIn } from 'formik';
import {
	has,
	join,
	length,
	map,
	mergeDeepRight,
	range,
	trim,
	uniq,
} from 'ramda';
import { isNotNilOrEmpty, isObject, isString } from 'ramda-adjunct';

import { insertObjectIf } from './common.util';

export const getMarkPassword = (value: string) =>
	join(
		'',
		map(() => '*', range(0, length(value)))
	);

export const convertCamelToServerError = (valueName: string) => {
	const capitalizedInput =
		valueName.charAt(0).toUpperCase() + valueName.slice(1);

	const errorCode = `has${capitalizedInput}ServerError`;

	return errorCode;
};

const convertCamelToMessageServerError = (valueName: string) => {
	const capitalizedInput =
		valueName.charAt(0).toUpperCase() + valueName.slice(1);

	const errorCode = `msg${capitalizedInput}ServerError`;

	return errorCode;
};

type WithErrorFlags<T> = T & {
	[K in keyof T as `has${Capitalize<string & K>}ServerError`]: boolean;
} & { [K in keyof T as `msg${Capitalize<string & K>}ServerError`]: string };

export const injectedFieldServerError = <
	T extends {
		[key: string]: any;
	},
>(
	initialValues: T
): WithErrorFlags<T> => {
	const result = { ...initialValues };

	for (const key in initialValues) {
		if (Object.hasOwn(initialValues, key)) {
			const convertedKey = convertCamelToServerError(key);
			const convertedMsgKey = convertCamelToMessageServerError(key);
			//@ts-expect-error: ignore error
			result[convertedKey] = false;
			//@ts-expect-error: ignore error
			result[convertedMsgKey] = '';
		}
	}

	return result;
};

type BlurEnterType = {
	onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	onPressEnter?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	onKeyDown?: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
};

interface GetFieldInputOutput {
	id: string;
	name: string;
	// onBlur: FocusEventHandler<HTMLInputElement>;
	onChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	value: string | number | readonly string[] | undefined;
	touched: boolean;
	errorCode: ErrorType;
	hasServerError: boolean;
	ref?: React.RefObject<any>;
	isUploadFileName?: boolean;
}

export const getFieldInputProps = <
	S extends FormikProps<{
		// eslint-disable-next-line
		[key in keyof S['values']]: any;
	}>,
>(
	formik: S,
	name: keyof S['values'],
	option?: {
		regex?: RegExp;
		blockEnter?: boolean;
		getAdditionalValues?: (
			value: string,
			prevValues: S['values']
		) => Partial<S['values']> | void;
		formatInput?: (value: string, number?: number) => void;
		inputRefs?: React.RefObject<InputRefs<S['values']>>;
		mode?: string;
		isUploadFileName?: boolean;
	}
): GetFieldInputOutput => {
	const { values, touched, errors, setValues } = formik;
	const {
		regex,
		blockEnter = false,
		getAdditionalValues,
		formatInput,
		inputRefs,
		isUploadFileName,
	} = option || {};

	const serverErrorKey = convertCamelToServerError(
		String(name)
	) as keyof S['values'];
	const msgServerErrorKey = convertCamelToMessageServerError(
		String(name)
	) as keyof S['values'];
	const isUpdate = formik.getFieldProps('isUpdate')?.value;

	const updateProps: BlurEnterType = {};
	if (isUpdate) {
		updateProps.onBlur = formik.submitForm;

		if (!blockEnter) {
			updateProps.onPressEnter = formik.submitForm;
		}
	}

	const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		let fileExtension = '';
		if (isUploadFileName) {
			fileExtension = getIn(values, String(name))?.split('.').pop() || '';
		}
		let value = e.target.value || '';
		let _value = value;

		if (isUploadFileName) {
			_value = `${value}.${fileExtension}`;
		}
		if (formatInput) {
			_value = formatInput(_value) as any;
		}

		if (_value && regex && !regex.test(_value)) return;

		setValues((prevValue) => ({
			...prevValue,
			[name]: _value,
			...insertObjectIf(Object.hasOwn(formik?.values, serverErrorKey), {
				[serverErrorKey]: false,
			}),
			...insertObjectIf(Object.hasOwn(formik?.values, msgServerErrorKey), {
				[msgServerErrorKey]: '',
			}),
			...(getAdditionalValues
				? getAdditionalValues(_value, prevValue) ?? {}
				: {}),
		}));
	};

	return {
		id: name as string,
		name: name as string,
		onChange: onChange,
		value: getIn(values, String(name)) || '',
		touched: getIn(touched, String(name)),
		errorCode:
			getIn(errors, String(name)) || getIn(values, msgServerErrorKey as string),
		// onBlur: _onBlur,
		hasServerError: getIn(values, serverErrorKey as string) || '',
		ref: inputRefs?.current?.[name],
		isUploadFileName,
		...updateProps,
	};
};

export const getNestedFieldInputProps = <
	S extends FormikProps<{
		// eslint-disable-next-line
		[key in keyof S['values']]: any;
	}>,
>(
	formik: S,
	path: string,
	option?: {
		regex?: RegExp;
		blockEnter?: boolean;
		getAdditionalValues?: (
			value: string,
			prevValues: S['values']
		) => Partial<S['values']> | void;
		formatInput?: (value: string, number?: number) => void;
		inputRefs?: React.RefObject<InputRefs<S['values']>>;
		mode?: string;
		isUploadFileName?: boolean;
	}
): GetFieldInputOutput => {
	const { values, touched, errors, setValues } = formik;
	const {
		regex,
		blockEnter = false,
		getAdditionalValues,
		formatInput,
		inputRefs,
		isUploadFileName,
	} = option || {};

	const paths = path.split('.');
	const rootKey = paths[0] as keyof S['values'];

	const serverErrorKey = convertCamelToServerError(
		String(rootKey)
	) as keyof S['values'];
	const msgServerErrorKey = convertCamelToMessageServerError(
		String(rootKey)
	) as keyof S['values'];
	const isUpdate = formik.getFieldProps('isUpdate')?.value;

	const updateProps: BlurEnterType = {};
	if (isUpdate) {
		updateProps.onBlur = formik.submitForm;

		if (!blockEnter) {
			updateProps.onPressEnter = formik.submitForm;
		}
	}

	const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		let fileExtension = '';
		if (isUploadFileName) {
			fileExtension = getIn(values, path)?.split('.').pop() || '';
		}
		let value = e.target.value || '';
		let _value = value;

		if (isUploadFileName) {
			_value = `${value}.${fileExtension}`;
		}
		if (formatInput) {
			_value = formatInput(_value) as any;
		}

		if (_value && regex && !regex.test(_value)) return;

		setValues((prevValue) => {
			const newValues = { ...prevValue };
			const currentValue = getIn(prevValue, paths.slice(0, -1).join('.')) || {};
			const lastKey = paths[paths.length - 1];
			const updatedValue = {
				...currentValue,
				[lastKey]: _value,
			};

			paths.slice(0, -1).reduce(
				(obj: { [key: string]: any }, currentPath, index) => {
					if (index === paths.length - 2) {
						obj[currentPath] = updatedValue;
					} else if (!obj[currentPath]) {
						obj[currentPath] = {};
					}
					return obj[currentPath];
				},
				newValues as { [key: string]: any }
			);

			return {
				...newValues,
				...insertObjectIf(Object.hasOwn(formik?.values, serverErrorKey), {
					[serverErrorKey]: false,
				}),
				...insertObjectIf(Object.hasOwn(formik?.values, msgServerErrorKey), {
					[msgServerErrorKey]: '',
				}),
				...(getAdditionalValues
					? getAdditionalValues(_value, prevValue) ?? {}
					: {}),
			};
		});
	};

	return {
		id: path,
		name: path,
		onChange: onChange,
		value: getIn(values, path) || '',
		touched: getIn(touched, path),
		errorCode:
			getIn(errors, path) || getIn(values, msgServerErrorKey as string),
		hasServerError: getIn(values, serverErrorKey as string) || '',
		ref: inputRefs?.current?.[rootKey],
		isUploadFileName,
		...updateProps,
	};
};

interface GetFieldCurrencyOutput {
	id: string;
	name: string;
	onValueChange: (value: string | undefined) => void;
	value: string;
	touched: boolean;
	errorCode: ErrorType;
	hasServerError: boolean;
	transformRawValue: (value: string) => string;
}

export const getFieldCurrencyProps = <
	S extends FormikProps<{
		// eslint-disable-next-line
		[key in keyof S['values']]: any;
	}>,
>(
	formik: S,
	name: keyof S['values'],
	option?: {
		formatInput?: (value: string, number?: number) => void;
		getAdditionalValues?: (
			value: string | undefined,
			prevValues: S['values']
		) => Partial<S['values']> | void;
	}
): GetFieldCurrencyOutput => {
	const { values, touched, errors, setValues } = formik;
	const { formatInput, getAdditionalValues } = option || {};

	const isUpdate = formik.getFieldProps('isUpdate')?.value;
	const updateProps: BlurEnterType = {};
	if (isUpdate) {
		updateProps.onBlur = formik.submitForm;

		updateProps.onKeyDown = (e) => {
			if (e.key === 'Enter') {
				formik.submitForm();
			}
		};
	}

	const serverErrorKey = convertCamelToServerError(
		String(name)
	) as keyof S['values'];
	const msgServerErrorKey = convertCamelToMessageServerError(
		String(name)
	) as keyof S['values'];

	const onValueChange = (value: string | undefined) => {
		setValues((prevValue) => ({
			...prevValue,
			[name]: value,
			...insertObjectIf(Object.hasOwn(formik?.values, serverErrorKey), {
				[serverErrorKey]: false,
			}),
			...insertObjectIf(Object.hasOwn(formik?.values, msgServerErrorKey), {
				[msgServerErrorKey]: '',
			}),
			...(getAdditionalValues
				? getAdditionalValues(value, prevValue) ?? {}
				: {}),
		}));
	};

	const transformRawValue = (value: string) => {
		if (formatInput) {
			return formatInput(value) as unknown as string;
		}
		return value;
	};

	return {
		id: name as string,
		name: name as string,
		onValueChange,
		value: String(getIn(values, String(name)) ?? '') as string,
		touched: getIn(touched, String(name)),
		errorCode:
			getIn(errors, String(name)) || getIn(values, msgServerErrorKey as string),
		// onBlur: _onBlur,
		hasServerError: getIn(values, serverErrorKey as string) || '',
		transformRawValue,
		...updateProps,
	};
};

interface GetFieldSelectOutput {
	name: string;
	value: string;
	onChange(value: string): void;
	// onOpenChange(open: boolean): void;
	touched: boolean;
	errorCode: string;
	hasServerError: boolean;
}

export const getFieldSelectProps = <
	S extends FormikProps<{
		// eslint-disable-next-line
		[key in keyof S['values']]: any;
	}>,
>(
	formik: S,
	name: keyof S['values'],
	option?: {
		defaultValue?: string;
		placeholder?: boolean;
		getAdditionalValues?: (
			value: string,
			prevValues: S['values']
		) => Partial<S['values']> | void;
		blockSubmit?: boolean;
		handleOnSave?: (value?: any, callback?: () => void) => void;
	}
): GetFieldSelectOutput => {
	const { values, touched, errors, setValues, initialValues } = formik;
	const {
		placeholder,
		defaultValue,
		getAdditionalValues,
		blockSubmit,
		handleOnSave,
	} = option || {};

	const isUpdate = formik.getFieldProps('isUpdate')?.value;

	const serverErrorKey = convertCamelToServerError(
		String(name)
	) as keyof S['values'];
	const msgServerErrorKey = convertCamelToMessageServerError(
		String(name)
	) as keyof S['values'];

	const _onValueChange = (value: string) => {
		setValues((prevValues) => ({
			...prevValues,
			[name]: value,
			...insertObjectIf(Object.hasOwn(formik?.values, serverErrorKey), {
				[serverErrorKey]: false,
			}),
			...insertObjectIf(Object.hasOwn(formik?.values, msgServerErrorKey), {
				[msgServerErrorKey]: '',
			}),
			...(getAdditionalValues
				? getAdditionalValues(value, prevValues) ?? {}
				: {}),
		}));

		handleOnSave?.(value, () => {
			setValues((prevValues) => ({
				...prevValues,
				[name]: initialValues[name],
			}));
		});

		if (isUpdate && !blockSubmit) {
			formik.submitForm();
		}
	};

	return {
		name: name as string,
		value:
			getIn(values, String(name)) || (placeholder ? null : defaultValue || ''),
		onChange: _onValueChange,
		touched: getIn(touched, String(name)),
		errorCode: getIn(errors, String(name)),
		hasServerError: getIn(values, serverErrorKey as string) || '',
	};
};

interface GetFieldAutoCompleteOutput {
	name: string;
	value: string;
	onChange(value: string): void;
	touched: boolean;
	errorCode: string;
	hasServerError: boolean;
}

export const getFieldAutoCompleteProps = <
	S extends FormikProps<{
		// eslint-disable-next-line
		[key in keyof S['values']]: any;
	}>,
>(
	formik: S,
	name: keyof S['values'],
	option?: {
		defaultValue?: string;
		blockEnter?: boolean;
		regex?: RegExp;
	}
): GetFieldAutoCompleteOutput => {
	const { values, touched, setValues, errors } = formik;
	const { blockEnter, regex } = option || {};
	const isUpdate = formik.getFieldProps('isUpdate')?.value;

	const serverErrorKey = convertCamelToServerError(
		String(name)
	) as keyof S['values'];
	const msgServerErrorKey = convertCamelToMessageServerError(
		String(name)
	) as keyof S['values'];

	const updateProps: BlurEnterType = {};

	const _onValueChange = (value: string) => {
		if (value && regex && !regex.test(value)) return;
		setValues((prevValue) => ({
			...prevValue,
			[name]: value,
			...insertObjectIf(Object.hasOwn(formik?.values, serverErrorKey), {
				[serverErrorKey]: false,
			}),
			...insertObjectIf(Object.hasOwn(formik?.values, msgServerErrorKey), {
				[msgServerErrorKey]: '',
			}),
		}));
	};

	if (isUpdate && !blockEnter) {
		updateProps.onKeyDown = (e) => {
			if (e.key === 'Enter') {
				formik.submitForm();
			}
		};
	}

	return {
		name: name as string,
		value: getIn(values, String(name)) || '',
		onChange: _onValueChange,
		touched: getIn(touched, String(name)),
		errorCode: getIn(errors, String(name)),
		hasServerError: getIn(values, serverErrorKey as string) || '',
		...updateProps,
	};
};

interface GetFieldCheckboxOutput {
	name: string;
	value: string;
	checked: boolean;
	onChange(value: CheckboxChangeEvent): void;
	touched: boolean;
	errorCode: string;
	hasServerError: boolean;
}

export const getFieldCheckboxProps = <
	S extends FormikProps<{
		// eslint-disable-next-line
		[key in keyof S['values']]: any;
	}>,
>(
	formik: S,
	name: keyof S['values'],
	option?: {
		getAdditionalValues?: (
			value: boolean,
			prevValues: S['values']
		) => Partial<S['values']> | void;
	}
): GetFieldCheckboxOutput => {
	const { values, touched, setValues, errors } = formik;
	const { getAdditionalValues } = option || {};
	const isUpdate = formik.getFieldProps('isUpdate')?.value;

	const serverErrorKey = convertCamelToServerError(
		String(name)
	) as keyof S['values'];

	const _onValueChange = (e: CheckboxChangeEvent) => {
		let value = e.target.checked;

		setValues((prevValues) => ({
			...prevValues,
			[name]: value,
			...insertObjectIf(Object.hasOwn(formik?.values, serverErrorKey), {
				[serverErrorKey]: false,
			}),
			...(getAdditionalValues
				? getAdditionalValues(value, prevValues) ?? {}
				: {}),
		}));

		if (isUpdate) {
			formik.submitForm();
		}
	};

	return {
		name: name as string,
		value: getIn(values, String(name)) || false,
		checked: !!(getIn(values, String(name)) || false),
		onChange: _onValueChange,
		touched: getIn(touched, String(name)),
		errorCode: getIn(errors, String(name)),
		hasServerError: getIn(values, serverErrorKey as string) || '',
	};
};

interface GetFieldRadioOutput {
	name: string;
	value: string;
	onChange(value: RadioChangeEvent): void;
	touched: boolean;
	errorCode: string;
	hasServerError: boolean;
}

export const getFieldRadioProps = <
	S extends FormikProps<{
		// eslint-disable-next-line
		[key in keyof S['values']]: any;
	}>,
>(
	formik: S,
	name: keyof S['values'],
	option?: {
		getAdditionalValues?: (
			value: string,
			prevValues: S['values']
		) => Partial<S['values']> | void;
	}
): GetFieldRadioOutput => {
	const { values, touched, setValues, errors } = formik;
	const isUpdate = formik.getFieldProps('isUpdate')?.value;
	const { getAdditionalValues } = option || {};

	const serverErrorKey = convertCamelToServerError(
		String(name)
	) as keyof S['values'];

	const _onValueChange = (e: RadioChangeEvent) => {
		let value = e.target.value;
		setValues((prevValues) => ({
			...prevValues,
			[name]: value,
			...insertObjectIf(Object.hasOwn(formik?.values, serverErrorKey), {
				[serverErrorKey]: false,
			}),
			...(getAdditionalValues
				? getAdditionalValues(value, prevValues) ?? {}
				: {}),
		}));
		if (isUpdate) {
			formik.submitForm();
		}
	};

	return {
		name: name as string,
		value: getIn(values, String(name)) ?? '',
		onChange: _onValueChange,
		touched: getIn(touched, String(name)),
		errorCode: getIn(errors, String(name)),
		hasServerError: getIn(values, serverErrorKey as string) || '',
	};
};

export const trimObjectValues = (data: any): any => {
	if (data instanceof FormData) return data;

	if (Array.isArray(data)) {
		return data.map((item) => trimObjectValues(item));
	}

	if (isObject(data) && isNotNilOrEmpty(data)) {
		return Object.entries(data).reduce(
			(acc, [key, value]) => ({
				...acc,
				[key]: isString(value)
					? trim(value)
					: isObject(value)
						? trimObjectValues(value)
						: value,
			}),
			{}
		);
	}

	return data;
};

export const setNestedValue = <T extends Record<string, any>>(
	obj: T,
	path: string,
	value: any
): T => {
	const newObj = { ...obj };
	const keys = path.split('.');
	let current: any = newObj;

	for (let i = 0; i < keys.length - 1; i++) {
		const key = keys[i];
		current[key] = current[key] ? { ...current[key] } : {};
		current = current[key];
	}

	const lastKey = keys[keys.length - 1];
	current[lastKey] = value;

	return newObj;
};

type GetFieldDateOutput = {
	id: string;
	name: string;
	onChange: DatePickerProps['onChange'] | TimePickerProps['onChange'];
	value: Dayjs | null;
	touched: boolean;
	errorCode: string;
	hasServerError: boolean;
	showTime?: boolean;
};

export const getFieldDateProps = <
	S extends FormikProps<{
		// eslint-disable-next-line unused-imports/no-unused-vars
		[key in keyof S['values']]: any;
	}>,
>(
	formik: S,
	name: keyof S['values'],
	option?: {
		timeOnly?: boolean;
		showTime?: boolean;
		format?: boolean;
		formatString?: string;
		getAdditionalValues?: (
			value: string | Dayjs,
			prevValues: S['values']
		) => Partial<S['values']> | void;
		onAfterDateChange?: (date: Dayjs | string | null) => void;
		validateDate?: (date: Dayjs | null) => boolean;
		blockSubmit?: boolean;
		handleOnSave?: (value?: any, callback?: () => void) => void;
	}
): GetFieldDateOutput => {
	const { values, touched, errors, setValues, initialValues } = formik;
	const {
		showTime = false,
		format = false,
		formatString = '',
		getAdditionalValues,
		onAfterDateChange,
		timeOnly,
		validateDate,
		blockSubmit = false,
		handleOnSave,
	} = option || {};

	const serverErrorKey = convertCamelToServerError(
		String(name)
	) as keyof S['values'];
	const msgServerErrorKey = convertCamelToMessageServerError(
		String(name)
	) as keyof S['values'];
	const isUpdate = formik.getFieldProps('isUpdate')?.value;

	const onChange:
		| DatePickerProps['onChange']
		| TimePickerProps['onChange'] = async (date, dateString) => {
		let outputFormat = 'YYYY-MM-DD';
		if (showTime) {
			outputFormat += ' HH:mm:ss';
		}
		if (timeOnly) {
			outputFormat = 'HH:mm';
		}
		if (formatString && isNotNilOrEmpty(formatString)) {
			outputFormat = formatString;
		}

		if (!validateDate || validateDate(date)) {
			const value = format ? formatDate(date, { outputFormat }) : date;

			setValues((prevValues) => {
				const newValues = setNestedValue(prevValues, String(name), value);

				const additionalValues: any = getAdditionalValues
					? getAdditionalValues(value, prevValues)
					: {};

				return {
					...mergeDeepRight(newValues, additionalValues),
					...insertObjectIf(Object.hasOwn(formik?.values, serverErrorKey), {
						[serverErrorKey]: false,
					}),
					...insertObjectIf(Object.hasOwn(formik?.values, msgServerErrorKey), {
						[msgServerErrorKey]: '',
					}),
				};
			});

			if (
				onAfterDateChange &&
				dateString !==
					dayjs(getIn(values, String(name)), SUPPORT_FORMAT_DATE_LIST).format(
						'YYYY-MM-DD'
					)
			) {
				onAfterDateChange?.(date);
			}

			handleOnSave?.(value, () => {
				setValues((prevValues) => ({
					...prevValues,
					[name]: initialValues[name],
				}));
			});

			if (isUpdate && !blockSubmit) {
				formik.submitForm();
			}
		}
	};

	return {
		id: name as string,
		name: name as string,
		onChange: onChange,
		value: getIn(values, String(name))
			? timeOnly
				? dayjs(getIn(values, String(name)), 'HH:mm')
				: dayjs(getIn(values, String(name)), SUPPORT_FORMAT_DATE_LIST) || null
			: null,
		touched: getIn(touched, String(name)),
		errorCode:
			getIn(errors, String(name)) || getIn(values, msgServerErrorKey as string),
		hasServerError: getIn(values, serverErrorKey as string) || '',
		showTime,
	};
};

type GetFieldTextEditorOutput = {
	id: string;
	name: string;
	value: ReactQuillValue | undefined;
	onChange?: (value: ReactQuillValue) => void;
	touched?: boolean;
	errorCode?: string | undefined;
	hasServerError?: boolean;
};

export const getFieldTextEditorProps = <
	S extends FormikProps<{
		// eslint-disable-next-line unused-imports/no-unused-vars
		[key in keyof S['values']]: any;
	}>,
>(
	formik: S,
	name: keyof S['values'],
	option?: {
		blockEnter?: boolean;
	}
): GetFieldTextEditorOutput => {
	const { values, touched, errors, setFieldValue } = formik;

	const { blockEnter = false } = option || {};

	const serverErrorKey = convertCamelToServerError(
		String(name)
	) as keyof S['values'];
	const msgServerErrorKey = convertCamelToMessageServerError(
		String(name)
	) as keyof S['values'];

	const isUpdate = formik.getFieldProps('isUpdate')?.value;

	const updateProps: BlurEnterType = {};
	if (isUpdate) {
		// updateProps.onBlur = formik.submitForm;
		if (!blockEnter) {
			updateProps.onPressEnter = formik.submitForm;
		}
	}

	const onChange: any = (value: ReactQuillValue) => {
		setFieldValue(String(name) as keyof S['values'], value);

		if (has(serverErrorKey, formik?.values)) {
			setFieldValue(String(serverErrorKey) as keyof S['values'], false);
		}

		if (has(msgServerErrorKey, formik?.values)) {
			setFieldValue(String(msgServerErrorKey) as keyof S['values'], '');
		}
	};

	return {
		id: name as string,
		name: name as string,
		onChange,
		value: getIn(values, String(name)) || '',
		touched: getIn(touched, String(name)),
		errorCode:
			getIn(errors, String(name)) || getIn(values, msgServerErrorKey as string),
		// onBlur: _onBlur,
		hasServerError: getIn(values, serverErrorKey as string) || '',
		...updateProps,
	};
};

type GetFieldAttachFileUploadOutput = {
	id: string;
	name: string;
	defaultFileList?: TUploadFile[];
	onFileChange?: (fileList: TUploadFile[]) => void;
	onFileRemove?: (id: number) => void;
};

export const getFieldAttachFileUploadProps = <
	S extends FormikProps<{
		// eslint-disable-next-line unused-imports/no-unused-vars
		[key in keyof S['values']]: any;
	}>,
>(
	formik: S,
	name: keyof S['values']
): GetFieldAttachFileUploadOutput => {
	const { values, setFieldValue, getFieldProps } = formik;
	const isUpdate = getFieldProps('isUpdate')?.value;

	const serverErrorKey = convertCamelToServerError(
		String(name)
	) as keyof S['values'];
	const msgServerErrorKey = convertCamelToMessageServerError(
		String(name)
	) as keyof S['values'];

	const onFileChange = (value: TUploadFile[]) => {
		const ids: number[] = value
			?.map((e: TUploadFile) => e.id || 0)
			.filter(Boolean);
		setFieldValue(
			String(name) as keyof S['values'],
			uniq([...(values[name] || []), ...ids])
		);

		if (has(serverErrorKey, formik?.values)) {
			setFieldValue(String(serverErrorKey) as keyof S['values'], false);
		}

		if (has(msgServerErrorKey, formik?.values)) {
			setFieldValue(String(msgServerErrorKey) as keyof S['values'], '');
		}
	};

	const onFileRemove = (value: number) => {
		const ids: number[] = values[name]?.filter((id: number) => id !== value);
		setFieldValue(String(name) as keyof S['values'], uniq(ids));
	};

	return {
		id: name as string,
		name: name as string,
		onFileChange,
		...insertObjectIf(!isUpdate, {
			onFileRemove,
		}),
		defaultFileList: getIn(values, String(name)) || [],
	};
};

export const hasAtLeastOneProperty = (obj: object, value: string) => {
	return Object.values(obj).some((v) => v === value);
};

type FormErrors = FormikErrors<{
	[key: string]: string;
}>;

export const hasErrorByKey = (
	errors: FormErrors,
	errorKey: FormError
): boolean => {
	return Object.values(errors || {}).some((error) => error === errorKey);
};

type Condition<T> = {
	check: () => boolean;
	errorMessage: string;
	errorField: keyof T;
};

export const validateDate = <T>(
	value: Dayjs | null,
	conditions: Condition<T>[],
	formik: FormikProps<T>
) => {
	for (const condition of conditions) {
		if (condition.check()) {
			showAlert(condition.errorMessage);
			// formik.setFieldValue(condition.errorField, true);
			return false;
		}
	}
	return true;
};

export const getHasServerErrorObject = (
	error: ApiResponseError,
	prefix: string = ''
) => {
	const errorFieldNames = error?.response?.data?.errorData?.fieldNames;

	const hasServerErrorObject = {} as any;

	if (errorFieldNames?.length) {
		errorFieldNames.forEach((fieldName) => {
			const camelCaseFieldName = toCamelCase(prefix + fieldName);
			const hasServerErrorKey = convertCamelToServerError(camelCaseFieldName);
			hasServerErrorObject[hasServerErrorKey] = true;
		});
	}

	return hasServerErrorObject;
};

export const setHasServerErrorObject = (
	error: ApiResponseError,
	setValues: (values: React.SetStateAction<any>) => void
) => {
	const errorFieldNames = error?.response?.data?.errorData?.fieldNames;

	if (errorFieldNames?.length) {
		const hasServerErrorObject = {} as any;

		errorFieldNames.forEach((fieldName) => {
			const camelCaseFieldName = toCamelCase(fieldName);
			const hasServerErrorKey = convertCamelToServerError(camelCaseFieldName);
			hasServerErrorObject[hasServerErrorKey] = true;
		});

		setValues((prevValues: any) => ({
			...prevValues,
			...hasServerErrorObject,
		}));
	}
};

export const getNestedFieldSelectProps = <
	S extends FormikProps<{
		// eslint-disable-next-line
		[key in keyof S['values']]: any;
	}>,
>(
	formik: S,
	path: string,
	option?: {
		getAdditionalValues?: (
			value: string,
			prevValues: S['values']
		) => Partial<S['values']> | void;
	}
): GetFieldSelectOutput => {
	const { values, touched, errors, setValues } = formik;
	const { getAdditionalValues } = option || {};

	const paths = path.split('.');
	const rootKey = paths[0] as keyof S['values'];

	const serverErrorKey = convertCamelToServerError(
		String(rootKey)
	) as keyof S['values'];
	const msgServerErrorKey = convertCamelToMessageServerError(
		String(rootKey)
	) as keyof S['values'];
	const isUpdate = formik.getFieldProps('isUpdate')?.value;

	const updateProps: BlurEnterType = {};
	if (isUpdate) {
		updateProps.onBlur = formik.submitForm;
	}

	const onChange = (value: string) => {
		setValues((prevValue) => {
			const newValues = { ...prevValue };
			const currentValue = getIn(prevValue, paths.slice(0, -1).join('.')) || {};
			const lastKey = paths[paths.length - 1];
			const updatedValue = {
				...currentValue,
				[lastKey]: value,
			};

			paths.slice(0, -1).reduce(
				(obj: { [key: string]: any }, currentPath, index) => {
					if (index === paths.length - 2) {
						obj[currentPath] = updatedValue;
					} else if (!obj[currentPath]) {
						obj[currentPath] = {};
					}
					return obj[currentPath];
				},
				newValues as { [key: string]: any }
			);

			return {
				...newValues,
				...insertObjectIf(Object.hasOwn(formik?.values, serverErrorKey), {
					[serverErrorKey]: false,
				}),
				...insertObjectIf(Object.hasOwn(formik?.values, msgServerErrorKey), {
					[msgServerErrorKey]: '',
				}),
				...(getAdditionalValues
					? getAdditionalValues(value, prevValue) ?? {}
					: {}),
			};
		});
	};

	return {
		name: path,
		onChange: onChange,
		value: getIn(values, path) || '',
		touched: getIn(touched, path),
		errorCode:
			getIn(errors, path) || getIn(values, msgServerErrorKey as string),
		hasServerError: getIn(values, serverErrorKey as string) || '',
		...updateProps,
	};
};

interface GetFieldSwitchOutput {
	name: string;
	value: boolean;
	onChange(checked: boolean): void;
	touched: boolean;
	errorCode: string;
	hasServerError: boolean;
}

export const getFieldSwitchProps = <
	S extends FormikProps<{
		// eslint-disable-next-line
		[key in keyof S['values']]: any;
	}>,
>(
	formik: S,
	name: keyof S['values'],
	option?: {
		getAdditionalValues?: (
			value: boolean,
			prevValues: S['values']
		) => Partial<S['values']> | void;
	}
): GetFieldSwitchOutput => {
	const { values, touched, errors, setValues } = formik;
	const { getAdditionalValues } = option || {};
	const isUpdate = formik.getFieldProps('isUpdate')?.value;

	const serverErrorKey = convertCamelToServerError(
		String(name)
	) as keyof S['values'];
	const msgServerErrorKey = convertCamelToMessageServerError(
		String(name)
	) as keyof S['values'];

	const onChange = (checked: boolean) => {
		setValues((prevValues) => ({
			...prevValues,
			[name]: checked,
			...insertObjectIf(Object.hasOwn(formik?.values, serverErrorKey), {
				[serverErrorKey]: false,
			}),
			...insertObjectIf(Object.hasOwn(formik?.values, msgServerErrorKey), {
				[msgServerErrorKey]: '',
			}),
			...(getAdditionalValues
				? getAdditionalValues(checked, prevValues) ?? {}
				: {}),
		}));

		if (isUpdate) {
			formik.submitForm();
		}
	};

	return {
		name: name as string,
		value: getIn(values, String(name)) || false,
		onChange,
		touched: getIn(touched, String(name)),
		errorCode:
			getIn(errors, String(name)) || getIn(values, msgServerErrorKey as string),
		hasServerError: getIn(values, serverErrorKey as string) || '',
	};
};
